import { RiMapPinLine } from "@itandi/itandi-bb-ui";
import { Badge } from "@itandi/itandi-bb-ui/Badge";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Breadcrumbs } from "@itandi/itandi-bb-ui/Breadcrumbs";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import cx from "clsx";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import { DealTypeLabel } from "~/components/common/parts/label/DealTypeLabel";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { Deal } from "~/models/Deal";
import { formatPropertyName } from "~/models/Deal";
import { isOriginVendor } from "~/models/DealVendorCompany";
import type { Message } from "~/models/Message";
import { getUnreadCount } from "~/models/Message";
import type { VendorSession } from "~/models/VendorSession";
import Styles from "./DealLayoutHeader.module.scss";
import { SimpleHeaderVendorSP } from "./simple_header/SimpleHeaderVendorSP";

type Props = {
	deal: Deal;
	enableChat: boolean;
	isChatOpen: boolean;
	items: ComponentProps<
		typeof Breadcrumbs | typeof SimpleHeaderVendorSP
	>["items"];
	messages: Message[];
	onToggleChat: () => void;
	session: VendorSession;
	useSimpleSpHeader: boolean;
};

export function DealLayoutHeaderVendor({
	deal,
	enableChat,
	isChatOpen,
	items,
	messages,
	onToggleChat,
	session,
	useSimpleSpHeader
}: Props) {
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const { match: isSingleColumn } = useMediaQuery(MEDIA_QUERY.singleColumn);

	const unreadMessageCount = useMemo((): number => {
		return getUnreadCount(
			messages,
			isOriginVendor(deal, session.vendorCompany.id)
				? "vendor_company"
				: "partner_vendor_company"
		);
	}, [deal, session, messages]);

	return (
		<>
			{useSimpleSpHeader && isSp ? (
				<SimpleHeaderVendorSP deal={deal} items={items} />
			) : (
				<div className={Styles.DealHeader} id="deal_header">
					<div className={Styles.HeaderLeftContent}>
						<Box marginBottom={16}>
							<Breadcrumbs items={items} />
						</Box>
						<div className={Styles.HeaderElements}>
							<div className={Styles.AlignCenter}>
								<DealTypeLabel
									dealSubtype={deal.dealSubtype}
									dealType={deal.dealType}
								/>
							</div>
							<div>
								<h1 className={Styles.RoomName}>{formatPropertyName(deal)}</h1>
							</div>
							<div className={cx(Styles.AlignCenter, Styles.DealAddress)}>
								<RiMapPinLine className={Styles.Icon} />
								{deal.address}
							</div>
						</div>
					</div>
					<Flex alignItems="flex-end" justifyContent="end" ml="auto">
						{enableChat && !isSingleColumn ? (
							<Button
								endIcon={
									unreadMessageCount > 0 ? (
										<Badge count={unreadMessageCount} variant="unread" />
									) : undefined
								}
								height="small"
								onClick={onToggleChat}
								variant="secondary"
							>
								{isChatOpen
									? "チャットを閉じる"
									: `管理会社とチャット${deal.messageSummaryUnread?.displayUnread && unreadMessageCount === 0 ? `（未読）` : ""}`}
							</Button>
						) : null}
					</Flex>
				</div>
			)}
		</>
	);
}
